import React from "react";
import {useTranslation} from "react-i18next";
import AccessTicketOptionStatusPill from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketOptionStatusPill";
import {driverName} from "utils/utility";
import {ReservationDriver} from "types/entities/ReservationDriver";
import {useAssignedItems} from "utils/hooks/useAssignedItems";
import FeatherIcon from "components/FeatherIcon";
import {useTypedSelector} from "helpers/reducers";
import {Tooltip} from "react-tooltip";
import {ArboCreditsLogo} from "pages/Front/Booking/Credits/CreditCalculator/components/icons";

export const AccessTicketDriversAssignationStatusItem = (props: {children: React.ReactNode | React.ReactNode[]}) => {
    return <div className={"AccessTicketDriversAssignationStatusItem flex flex-row items-center justify-between gap-x-1.5 relative"}>{props.children}</div>;
};

export const StatusCheck = (props: {children: React.ReactNode | React.ReactNode[]}) => {
    return <div className={"AccessTicketDriversAssignationStatusCheck basis-[80px] flex justify-center"}>{props.children}</div>;
};

export const AccessTicketDriversAssignationStatusItemSwitcher = (props: {reservationDriver: ReservationDriver; children: React.ReactNode}) => {
    const {t} = useTranslation();
    const user = useTypedSelector(state => state.bookingLoginState.user);
    const reservationDriver = props.reservationDriver;
    const isMainUser: boolean = user?.id === props?.reservationDriver?.driver?.id;
    const userHasBirthdateAndNickname: boolean = !!(props?.reservationDriver?.driver?.nickname && props?.reservationDriver?.driver?.birthdate);
    const userHasAllRequiredData: boolean = !!(
        props?.reservationDriver?.driver?.name &&
        props?.reservationDriver?.driver?.last_name &&
        props?.reservationDriver?.driver?.nickname &&
        props?.reservationDriver?.driver?.birthdate &&
        props?.reservationDriver?.driver?.address &&
        props?.reservationDriver?.driver?.zip &&
        props?.reservationDriver?.driver?.town &&
        props?.reservationDriver?.driver?.country &&
        props?.reservationDriver?.driver?.email &&
        props?.reservationDriver?.driver?.phone
    );

    const missingFields: string[] = [];
    const driver = props?.reservationDriver?.driver;

    if (!driver?.name) missingFields.push(t("Name"));
    if (!driver?.last_name) missingFields.push(t("Last name"));
    if (!driver?.nickname) missingFields.push(t("Nickname"));
    if (!driver?.birthdate) missingFields.push(t("Birthdate"));
    if (!driver?.address) missingFields.push(t("Address"));
    if (!driver?.zip) missingFields.push(t("ZIP"));
    if (!driver?.town) missingFields.push(t("City"));
    if (!driver?.country) missingFields.push(t("Country"));
    if (!driver?.email) missingFields.push(t("Email"));
    if (!driver?.phone) missingFields.push(t("Phone"));

    const toolTipContent = missingFields.length > 0 ? `${t("Missing")}: ${missingFields.join(", ")}` : "";
    const creditLimitPill =
        typeof reservationDriver.credit_limit === "number" ? (
            <AccessTicketOptionStatusPill className={"bg-arbo-red/10 border border-arbo-red/20"}>
                <span className={"flex items-center gap-x-1 font-medium whitespace-nowrap"}>
                    <ArboCreditsLogo size={18} className={"shrink-0"} />
                    <span>{`Limit ${reservationDriver.credit_limit}`}</span>
                </span>
            </AccessTicketOptionStatusPill>
        ) : null;

    if (reservationDriver.invited_at === null || reservationDriver.is_invited === false) {
        return (
            <AccessTicketDriversAssignationStatusItem>
                <div className={"flex flex-row flex-wrap items-center gap-2"}>
                    <AccessTicketOptionStatusPill className={"bg-gray-200 !text-xs md:!text-[20px] md:!leading-normal font-medium"}>
                        <span>{reservationDriver.driver ? driverName(reservationDriver.driver) : "----"}</span>
                    </AccessTicketOptionStatusPill>
                </div>

                <div className={"grow-0 flex flex-row gap-x-3 items-center justify-end"}>
                    {props.children}
                    {creditLimitPill}

                    <StatusCheck>
                        {/*{(isMainUser && userHasBirthdateAndNickname || !isMainUser)*/}
                        {userHasAllRequiredData ? (
                            <FeatherIcon className={"size-[20px] md:size-[30px] text-arbo-green"} name={"check"} strokeWidth={2} />
                        ) : (
                            <>
                                <span data-tooltip-id={"status-tooltip-" + reservationDriver.id} data-tooltip-content={toolTipContent}>
                                    <FeatherIcon className={"size-[20px] md:size-[30px] text-arbo-amber"} name={"alert-circle"} strokeWidth={2} />
                                </span>
                                <Tooltip id={"status-tooltip-" + reservationDriver.id} />
                            </>
                        )}
                    </StatusCheck>
                </div>
                {!userHasAllRequiredData && <div className={"md:hidden absolute text-xxs bottom-[-20px] right-0 text-arbo-amber"}>{toolTipContent}</div>}
            </AccessTicketDriversAssignationStatusItem>
        );
    }

    return (
        <AccessTicketDriversAssignationStatusItem>
            <div className={"flex flex-row flex-wrap items-center gap-2"}>
                <AccessTicketOptionStatusPill className={"bg-arbo-yellow/50"}>
                    <span>
                        {t("Invited")}: {reservationDriver.identifier}
                    </span>
                </AccessTicketOptionStatusPill>
                {creditLimitPill}
            </div>
            <div className={"grow-0 flex flex-row gap-x-3 items-center justify-end"}>
                {props.children}
                <StatusCheck>
                    <FeatherIcon className={"size-[20px] md:size-[30px] text-arbo-yellow"} name={"clock"} strokeWidth={2} />
                </StatusCheck>
            </div>
        </AccessTicketDriversAssignationStatusItem>
    );
};

export const AccessTicketDriversAssignationStatusDriver = (props: {reservationDriver: ReservationDriver}) => {
    const {reservationDriverUpsellItems} = useAssignedItems(props.reservationDriver);

    return (
        <div className="AccessTicketDriversAssignationStatusDriver flex flex-col items-stretch justify-center w-full pl-[51px] md:pl-[81px] pr-[12px] md:pr-[16px] pt-[27px] md:pt-[33px] pb-[27px] md:pb-[33px]">
            <AccessTicketDriversAssignationStatusItemSwitcher reservationDriver={props.reservationDriver}>
                <span>
                    {reservationDriverUpsellItems.length > 0 && (
                        <FeatherIcon className={"size-[20px] md:size-[30px] text-brand-yellow"} name={"shopping-bag"} strokeWidth={2} />
                    )}
                </span>
            </AccessTicketDriversAssignationStatusItemSwitcher>
        </div>
    );
};
